import { Container, Graphics } from 'pixi.js';
import type { InteractiveObject } from '@duck/contracts';
import { darken, lighten, mix, type ThemeArt } from './theme-art';
import { bardStage, cauldron, diceTable, lectern, pew, tapestry } from './castle-kit';

/**
 * Procedural PROP bodies for the INTERACTIVE objects (the things you press E on). Castle Hearthhold
 * (ADR-0051) re-themes each engine KIND to its medieval prop, drawn from the shared {@link './castle-kit'}
 * so an interactive prop matches the bespoke scenery around it:
 *   • coffee  → the Kitchen CAULDRON (a warm break by the fire);
 *   • desk    → a Scriptorium LECTERN (illuminating a manuscript = working);
 *   • sofa    → the Barracks fireside SETTLE (a wooden bench);
 *   • tv      → the War Room CAMPAIGN MAP (a woven wall map — the "presentation screen");
 *   • arcade  → the Tavern GAMES table (dice/cards);
 *   • jukebox → the Tavern BARD'S STAGE (toggles the room disco).
 *
 * Coordinates are LOCAL to a container the map positions at the object's (x,y). The map layer also draws
 * the soft interaction pad + the dual-coded icon + text label above this body (the affordance contract is
 * untouched). Built ONCE on map build/re-skin — never per frame (Performance-Engineer gate). The Furniture
 * Store (ADR-0050) re-uses these bodies for placed interactive furniture, so placed props stay on-theme.
 */
export function buildPropBody(obj: InteractiveObject, art: ThemeArt): Container {
  switch (obj.kind) {
    case 'coffee':
      return cauldron(art);
    case 'desk':
      return lectern(art);
    case 'sofa':
      return pew(art, 72);
    case 'tv':
      return tapestry(art, 96, 74, 'map');
    case 'arcade':
      return diceTable(art, 38);
    case 'jukebox':
      return bardStage(art);
    default:
      return new Container();
  }
}

/** A leafy potted plant (decor) — three soft leaf lobes over a pot. Static; drawn once. */
export function buildPlant(art: ThemeArt): Graphics {
  const leaf = mix(0x3f9d52, art.accent, 0.12);
  return new Graphics()
    .ellipse(0, 16, 12, 5)
    .fill({ color: 0x000000, alpha: 0.2 })
    .rect(-7, 4, 14, 12)
    .fill(mix(0x9c5a2e, art.wall, 0.2))
    .rect(-7, 4, 14, 3)
    .fill({ color: 0xffffff, alpha: 0.12 })
    .ellipse(0, -10, 12, 18)
    .fill(leaf)
    .ellipse(-9, -2, 9, 13)
    .fill(lighten(leaf, 0.1))
    .ellipse(9, -2, 9, 13)
    .fill(darken(leaf, 0.12));
}

/** A small bookshelf / storage unit (decor) — shelves with colourful spines. Static; drawn once. */
export function buildBookshelf(art: ThemeArt): Graphics {
  const frame = mix(0x6a4a2b, art.wall, 0.3);
  const g = new Graphics()
    .ellipse(0, 30, 24, 6)
    .fill({ color: 0x000000, alpha: 0.2 })
    .roundRect(-22, -28, 44, 58, 3)
    .fill(frame)
    .roundRect(-22, -28, 44, 3, 3)
    .fill({ color: 0xffffff, alpha: 0.1 });
  const spines = [lighten(art.accent, 0.1), 0xff7a6b, 0x5ad1ff, 0x95e06c, 0xffd23f];
  for (let row = 0; row < 3; row++) {
    const y = -22 + row * 18;
    g.rect(-19, y + 13, 38, 2).fill(darken(frame, 0.4));
    for (let i = 0; i < 7; i++) {
      const h = 11 + (i % 3) * 2;
      g.rect(-18 + i * 5, y + 12 - h, 4, h).fill(spines[(row * 7 + i) % spines.length]!);
    }
  }
  return g;
}
